import {useSuspenseQuery} from "@apollo/client";
import isEmpty from "lodash/isEmpty";
import {toast} from "react-hot-toast";

import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {CheckoutContextResult, GQL_CHECKOUT_CONTEXT} from "@/lib/queries/billing.ts";
import {AccountSubscriptionType, AccountType} from "@/lib/types/accounts.ts";
import {BillingPeriod} from "@/lib/types/billing.ts";
import {InvoicesList} from "@/pages/billing/invoices/InvoicesList.tsx";
import {PaymentMethod} from "@/pages/billing/payment-method/PaymentMethod.tsx";
import {CancelSubscription} from "@/pages/billing/subscription-summary/CancelSubscription.tsx";
import {ChangeToAnnually} from "@/pages/billing/subscription-summary/ChangeToAnnually.tsx";
import {ChangeToMonthly} from "@/pages/billing/subscription-summary/ChangeToMonthly.tsx";
import {SubscriptionSummaryCustomer} from "@/pages/billing/subscription-summary/SubscriptionSummaryCustomer.tsx";
import {SubscriptionSummaryFreeTrial} from "@/pages/billing/subscription-summary/SubscriptionSummaryFreeTrial.tsx";
import {ChangeToAnnually as ChangeToAnnuallyV2} from "@/pages/billing/v2/ChangeToAnnually.tsx";

export const SubscriptionSummary = () => {
  const {user} = useCurrentUserContext();
  const {
    data: {
      checkoutContext: {paymentMethod},
    },
  } = useSuspenseQuery<CheckoutContextResult>(GQL_CHECKOUT_CONTEXT);
  const isMonthly = user!.account.billing_period === BillingPeriod.MONTH;
  const freeTrial = !isEmpty(user?.account.free_trial_expires_at);
  const regularAccount = user?.account.type === AccountType.REGULAR;
  const invoicedAccount = user?.account.type === AccountType.INVOICED;
  const accountCanCancel = regularAccount || invoicedAccount;
  const noPaymentMethod = !paymentMethod?.type;
  const ownershipTransfer = user?.has_admin_access && noPaymentMethod;
  const canEdit = user?.is_primary || ownershipTransfer;
  return (
    <div>
      <h3 className="my-4 fs-5">Subscription Summary</h3>
      {freeTrial ? <SubscriptionSummaryFreeTrial /> : <SubscriptionSummaryCustomer />}
      <h3 className="my-4 fs-5">Payment Method</h3>
      <PaymentMethod
        readOnly={!canEdit}
        withButtons={true}
        onSuccess={() => {
          toast.success(
            <>
              <strong>Success</strong> — Your payment method has been updated.
            </>
          );
        }}
        allowEmpty={true}
        variant="modal"
        formId={"paymentMethodForm"}
        subscription={false}
      />
      {!freeTrial && user?.is_primary && regularAccount && (
        <div className="my-4">
          {isMonthly ? (
            user?.account.subscription_type === AccountSubscriptionType.BILLING_2_0 ? (
              <ChangeToAnnuallyV2 />
            ) : (
              <ChangeToAnnually currentPlan={user.account.plan} />
            )
          ) : (
            <ChangeToMonthly currentPlan={user.account.plan} />
          )}
        </div>
      )}
      {user?.has_admin_access && (
        <>
          <h3 className="my-4 fs-5">Invoices</h3>
          <InvoicesList />
        </>
      )}
      {!freeTrial && user?.is_primary && accountCanCancel && (
        <div className="my-4">
          <CancelSubscription />
        </div>
      )}
    </div>
  );
};
